import { getBestPlayerBoxScore } from "./getBestPlayerBoxScore.ts";

/**
 * G3 决策归因 (UX_REWORK_PLAN_2026-07-14 §G3 / GAME_PRODUCT_DESIGN_REVIEW §二 D4).
 *
 * "游戏从不告诉玩家'你的决定起作用了'" — after a completed game, surface a single
 * line crediting the human GM for a recently-acquired player's box score. Pure
 * derivation over data ZenGM already persists (player.transactions + per-team
 * stats rows); no new persistent structure, no sim change.
 *
 * Attribution fires only for a player who:
 *   1. currently sits on the user's roster AND appears in the game's box score
 *      with minutes (`min > 0` — DNPs have nothing to attribute);
 *   2. was acquired by the user's team via **trade or free-agent signing**
 *      (`player.transactions` entry `type: "trade" | "freeAgent"` whose `tid`
 *      is the user's team). Drafted rookies, re-signings (never pushed as a
 *      freeAgent txn by sign.ts) and opening-roster players (createStream sets
 *      `transactions = []`) are excluded — so this cleanly separates a *player
 *      operation* from an auto/opening acquisition (G3-2);
 *   3. is a **new arrival for the current season**: the per-team stats row is
 *      unique per (pid, tid, season, playoffs), and addStatsRow stamps
 *      `yearsWithTeam === 1` on the first season with a team. A long-tenured
 *      player (acquired seasons ago) resets to a small `gp` each season but
 *      carries `yearsWithTeam > 1`, so this gate excludes them;
 *   4. is inside the attribution window: cumulative games for the user's team
 *      this season (`gp`, regular + playoff) ≤ ATTRIBUTION_WINDOW_GAMES.
 *
 * When several new arrivals played, the best box score (getBestPlayerBoxScore,
 * the same DRE-style ranking as the last-game key player) wins — a single row,
 * never a stack.
 *
 * `debut` is asserted only when it is provable: `gp === 1` means the player has
 * exactly one game for the team this season, so any box score showing them is
 * necessarily that debut. For a later game in the window the phrasing degrades
 * to the non-debut variant rather than fabricate a "首秀" claim.
 */

/** First N games after joining that a new arrival is credited (N ≤ 5). */
export const ATTRIBUTION_WINDOW_GAMES = 5;

export type GameAttribution = {
	/** Acquisition verb: trade ("你换来的") vs free-agent signing ("你签下的"). */
	type: "trade" | "freeAgent";
	name: string;
	/** Provably the player's first game for the team this season (gp === 1). */
	debut: boolean;
	/** Top box score components for this game (same shape as lastGame.keyPlayer). */
	stats: { stat: string; value: number }[];
};

type TransactionLike = {
	type: string;
	tid: number;
};

type StatsRowLike = {
	season: number;
	tid: number;
	playoffs: boolean;
	gp?: number;
	yearsWithTeam?: number;
};

export type AttributionPlayer = {
	transactions?: TransactionLike[];
	stats: StatsRowLike[];
};

// Latest trade/free-agent acquisition to the user's team, if any. transactions
// are pushed chronologically, so the last matching entry is the most recent
// (e.g. traded away then signed back → "你签下的").
const getUserAcquisitionType = (
	p: AttributionPlayer,
	userTid: number,
): "trade" | "freeAgent" | undefined => {
	if (!p.transactions) {
		return undefined;
	}

	let type: "trade" | "freeAgent" | undefined;
	for (const txn of p.transactions) {
		if ((txn.type === "trade" || txn.type === "freeAgent") && txn.tid === userTid) {
			type = txn.type;
		}
	}
	return type;
};

/**
 * Pure attribution derivation for one game's user-team box score. `players`
 * maps pid → the current player record (transactions + stats) so this stays
 * free of idb; the worker views build the map from idb.cache and gate on
 * season/tid before calling.
 */
export const deriveGameAttribution = ({
	userTeamPlayers,
	players,
	season,
	userTid,
}: {
	userTeamPlayers: unknown;
	players: Map<number, AttributionPlayer>;
	season: number;
	userTid: number;
}): GameAttribution | undefined => {
	if (!Array.isArray(userTeamPlayers)) {
		return undefined;
	}

	const candidates: {
		boxPlayer: any;
		type: "trade" | "freeAgent";
		gp: number;
	}[] = [];

	for (const boxPlayer of userTeamPlayers) {
		const min = typeof boxPlayer?.min === "number" ? boxPlayer.min : 0;
		if (min <= 0) {
			continue;
		}

		const pid = boxPlayer.pid;
		if (typeof pid !== "number") {
			continue;
		}

		const p = players.get(pid);
		if (!p) {
			continue;
		}

		const type = getUserAcquisitionType(p, userTid);
		if (!type) {
			continue;
		}

		const rows = p.stats.filter(
			(row) => row.season === season && row.tid === userTid,
		);
		if (rows.length === 0) {
			continue;
		}

		// Recency: first season with this team (a new arrival for THIS season).
		const regRow = rows.find((row) => !row.playoffs) ?? rows[0]!;
		if (regRow.yearsWithTeam !== 1) {
			continue;
		}

		// Window: games for the user's team this season (regular + playoff).
		const gp = rows.reduce(
			(sum, row) => sum + (typeof row.gp === "number" ? row.gp : 0),
			0,
		);
		if (gp < 1 || gp > ATTRIBUTION_WINDOW_GAMES) {
			continue;
		}

		candidates.push({ boxPlayer, type, gp });
	}

	if (candidates.length === 0) {
		return undefined;
	}

	const best = getBestPlayerBoxScore(candidates.map((c) => c.boxPlayer));
	// No positive box score components → nothing worth crediting.
	if (!best || best.statTexts.length === 0) {
		return undefined;
	}

	const bestCandidate =
		candidates.find((c) => c.boxPlayer.pid === best.p.pid) ?? candidates[0]!;

	return {
		type: bestCandidate.type,
		name: best.p.name,
		debut: bestCandidate.gp === 1,
		stats: best.statTexts.slice(0, 3).map((stat) => ({
			stat,
			value: best.processedStats[stat],
		})),
	};
};
